import { useAuth } from '../contexts/AuthContext';
import { useMemo } from 'react';

// Default subscription for unauthenticated users
const DEFAULT_SUBSCRIPTION = {
  whizz_plan: 'free',
  whizz_status: 'active',
  whizz_stories_used: 0,
  whizz_stories_remaining: 3,
  whizz_stories_limit: 3
};

export const useUserData = () => {
  const { 
    userData, 
    userDataLoading, 
    userDataError, 
    refreshUserData,
    updateUserData,
    updateQuotaAfterStoryGeneration,
    currentUser
  } = useAuth();

  // Use default subscription when no user data available
  const subscription = useMemo(() => {
    if (userData?.subscription) {
      return userData.subscription;
    }
    // Return default for unauthenticated or loading state
    return DEFAULT_SUBSCRIPTION;
  }, [userData?.subscription]);

  const stories = userData?.stories || [];
  const credits = userData?.credits || null;

  // Helper functions (same as useUserStatus)
  const hasAvailableStories = useMemo(() => {
    return subscription?.whizz_stories_remaining > 0;
  }, [subscription?.whizz_stories_remaining]);

  const shouldShowPaymentButton = useMemo(() => {
    return subscription?.whizz_stories_remaining === 0;
  }, [subscription?.whizz_stories_remaining]);

  const getStatusText = useMemo(() => {
    // Always show data, never "Loading..." for better UX
    const plan = subscription?.whizz_plan || 'free';
    const used = subscription?.whizz_stories_used || 0;
    const limit = subscription?.whizz_stories_limit || 3;
    
    const isFr = (navigator.language || 'en').toLowerCase().startsWith('fr');
    if (plan === 'free') {
      return isFr ? `Gratuit ${used}/${limit}` : `Free ${used}/${limit}`;
    } else if (plan === 'standard') {
      return `Standard ${used}/${limit}`;
    } else if (plan === 'on_demand') {
      return isFr ? "À la demande - Payez par histoire" : 'On-demand - Pay per story';
    }
    
    return isFr ? `Gratuit ${used}/${limit}` : `Free ${used}/${limit}`;
  }, [subscription]);

  const getButtonText = (defaultText) => {
    if (shouldShowPaymentButton) {
      return (navigator.language || 'en').toLowerCase().startsWith('fr') ? 'Acheter une nouvelle histoire' : 'Buy a new Story';
    }
    return defaultText;
  };

  const getButtonClasses = (defaultClasses) => {
    if (shouldShowPaymentButton) {
      // Yellow button style for quota exceeded
      return 'w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-3 px-6 rounded-xl font-bold text-base transition-all duration-200 transform hover:scale-[1.02] flex items-center justify-center gap-3';
    }
    return defaultClasses;
  };

  return {
    // Direct access to data
    subscription,
    stories,
    credits,
    
    // Computed helpers
    hasAvailableStories,
    shouldShowPaymentButton,
    getStatusText,
    getButtonText,
    getButtonClasses,
    
    // Raw userData access for advanced use cases
    userData,
    
    // User state
    isAuthenticated: !!currentUser,
    
    // Functions
    loading: userDataLoading,
    refreshUserData,
    updateQuotaAfterStoryGeneration
  };
};